import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams } from 'react-router-dom';
import UitslagenTabel from './UitslagenTabel';
import {
  useUitslagenData,
  filterRows as filterRowsHelper,
  collapseNkTeamRows,
  years,
} from '../hooks/useUitslagenData';

function UitslagenArchief() {
  // Haal data altijd zelf op - werkt zowel in WordPress als localhost
  const {
    uitslagenData,
    uitslagenMeta,
    uitslagenErrors,
    loading,
  } = useUitslagenData();
  
  const [searchParams, setSearchParams] = useSearchParams();
  
  const allTabs = [...years].reverse();
  const recentTabs = allTabs.slice(0, 6); // Laatste 6 jaren als tabs
  const olderTabs = allTabs.slice(6); // Rest voor dropdown

  // Lees initiële waarden uit URL
  const urlYear = searchParams.get('jaar');
  const urlSearch = searchParams.get('zoek') || '';
  const urlType = searchParams.get('serie') || 'alle';
  const urlGender = searchParams.get('geslacht') || 'alle';

  const [activeTab, setActiveTab] = useState(() => {
    const year = urlYear ? Number(urlYear) : null;
    return year && allTabs.includes(year) ? year : allTabs[0];
  });
  const [searchTerm, setSearchTerm] = useState(urlSearch);
  const [typeFilter, setTypeFilter] = useState(urlType);
  const [genderFilter, setGenderFilter] = useState(urlGender);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth <= 1024);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setTypeFilter('alle');
    setGenderFilter('alle');
  }, [activeTab]);

  // Gebruik useMemo voor activeRows:
  const activeRows = useMemo(
    () => (uitslagenData && uitslagenData[activeTab]
      ? uitslagenData[activeTab]
      : []),
    [uitslagenData, activeTab],
  );

  const hasNk = activeRows.some((row) => row.type === 'nk');
  const showNkFilter = hasNk && Number(activeTab) >= 2026;

  useEffect(() => {
    if (!showNkFilter && typeFilter === 'nk') {
      setTypeFilter('alle');
    }
  }, [showNkFilter, typeFilter]);
  const availableTypes = new Set(activeRows.map((row) => row.type).filter(Boolean));
  const hasTypeFilter = availableTypes.size > 1;
  const hasGender = activeRows.some((row) => row.categorie);
  const hasFinale = activeRows.some((row) => row.finale && row.finale.trim() !== '');

  const rows = useMemo(() => {
    const isNonFinishRow = (row) => {
      const statusTokens = [
        (row.plaats || ''),
        (row.totaaltijd || ''),
        (row.status || ''),
      ]
        .map((v) => String(v).trim().toLowerCase());
      return statusTokens.some((v) => ['dnf', 'dns', 'dsq', 'dq'].includes(v));
    };

    let r = activeRows;
    if (typeFilter === 'finale') {
      r = r.filter((row) => row.finale && row.finale.trim() !== '');
    } else if (typeFilter !== 'alle') {
      r = r.filter((row) => row.type === typeFilter);
    }
    if (genderFilter !== 'alle') {
      r = r.filter((row) => {
        const c = (row.categorie || '').toLowerCase();
        if (genderFilter === 'man') return c === 'm';
        if (genderFilter === 'vrouw') return c === 'v';
        if (genderFilter === 'onbekend') return c && c !== 'm' && c !== 'v';
        return c === genderFilter;
      });
    }

    // Sorteer eerst complete set (na type/geslacht filters), zodat rangnummers stabiel blijven.
    const sorted = filterRowsHelper(r, '');
    const nkRows = sorted.filter((row) => row.type === 'nk');
    const nonNkRows = sorted.filter((row) => row.type !== 'nk');
    const hasBothTypesInSelection = nonNkRows.some((row) => row.type === 'dubbel')
      && nonNkRows.some((row) => row.type === 'enkel');
    const preferredType = [2024, 2025, 2026].includes(Number(activeTab)) ? 'dubbel' : 'enkel';
    const sortedWithTypePriority = typeFilter === 'alle' && nkRows.length > 0
      ? [
        ...nkRows,
        ...(hasBothTypesInSelection
          ? [
            ...nonNkRows.filter((row) => row.type === preferredType),
            ...nonNkRows.filter((row) => row.type !== preferredType),
          ]
          : nonNkRows),
      ]
      : sorted;

    const categoryCounters = {};
    let overallRank = 0;
    const collapsedRows = collapseNkTeamRows(sortedWithTypePriority);

    r = collapsedRows.map((row) => {
      const catKey = (row.categorie || '').toUpperCase();
      if (isNonFinishRow(row)) {
        return {
          ...row,
          plaats: 'DNF',
          plaatsCategorie: '-',
        };
      }

      overallRank += 1;
      const nextRank = (categoryCounters[catKey] || 0) + 1;
      categoryCounters[catKey] = nextRank;

      return { ...row, plaats: overallRank, plaatsCategorie: nextRank };
    });

    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return r;

    const lower = normalizedSearch;
    return r.filter((row) => {
      const naam = row.naam ? row.naam.toString().toLowerCase() : '';
      const partner = row.partner ? row.partner.toString().toLowerCase() : '';
      const team = row.team ? row.team.toString().toLowerCase() : '';
      const woonplaats = row.woonplaats ? row.woonplaats.toString().toLowerCase() : '';
      const startnr = row.startnummer ? row.startnummer.toString().toLowerCase() : '';
      return naam.includes(lower)
        || partner.includes(lower)
        || team.includes(lower)
        || woonplaats.includes(lower)
        || startnr.includes(lower);
    });
  }, [activeRows, searchTerm, typeFilter, genderFilter, activeTab]);

  // Definieer categorieOpties veilig boven de render:
  const categorieOpties = useMemo(() => Array.from(new Set(activeRows.map(row => (row.categorie || '').toUpperCase()).filter(Boolean))), [activeRows]);

  return (
    <div className="app" style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 1em', boxSizing: 'border-box' }}>
      <header className="app-header">
        <h1>
          <a name="eureka" href="#eureka">EUREKA</a>
          <span style={{ fontSize: '0.5em', fontWeight: 'normal', marginLeft: '0.7em', color: '#fff' }}>
            v{import.meta.env.VITE_APP_VERSION} β
          </span>
        </h1>
        <div style={{ fontSize: '1.1em', marginBottom: '0.5em' }}>
          <span style={{ fontWeight: 'bold' }}>E</span>ilanden <span style={{ fontWeight: 'bold' }}>U</span>itslagen <span style={{ fontWeight: 'bold' }}>RE</span>gistratie &amp; <span style={{ fontWeight: 'bold' }}>K</span>ennis <span style={{ fontWeight: 'bold' }}>A</span>rchief
        </div>
        <div style={{ fontSize: '0.95em', color: '#fff', marginBottom: '0.5em' }}>
          <span style={{ color: '#c00', fontWeight: 'bold' }}>Eureka is nog in Beta.</span> Mail fouten in uitslagen of feature requests bij <a href="mailto:bartvanderwal@gmail.com" style={{ color: '#fff', textDecoration: 'underline' }}>Bart van der Wal</a>. Check de originele uitslag bij twijfel.
        </div>
        {isMobile ? (
          <select value={activeTab} onChange={(e) => setActiveTab(Number(e.target.value))} style={{ fontSize: '1.1em', marginBottom: '1em', padding: '0.5em 1em' }}>
            {allTabs.map((tab) => (
              <option key={tab} value={tab}>{`Uitslag ${tab}`}</option>
            ))}
          </select>
        ) : (
          <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="tabs">
              {recentTabs.map((tab) => (
                <button
                  key={tab}
                  className={tab === activeTab ? 'active-tab' : ''}
                  onClick={() => setActiveTab(tab)}
                  type="button"
                >
                  {`Uitslag ${tab}`}
                </button>
              ))}
            </div>
            {olderTabs.length > 0 && (
              <select
                value={olderTabs.includes(activeTab) ? activeTab : ''}
                onChange={(e) => setActiveTab(Number(e.target.value))}
                style={{
                  fontSize: '0.95em',
                  padding: '0.5em 0.8em',
                  border: '1px solid var(--primary-blue)',
                  borderRadius: '6px',
                  background: olderTabs.includes(activeTab) ? 'var(--accent-green)' : 'var(--primary-blue-light)',
                  color: olderTabs.includes(activeTab) ? '#222' : 'var(--primary-blue-dark)',
                  fontWeight: olderTabs.includes(activeTab) ? 'bold' : 'normal',
                  cursor: 'pointer',
                  marginTop: '-13px',
                }}
              >
                <option value="" disabled>Oudere jaren...</option>
                {olderTabs.map((tab) => (
                  <option key={tab} value={tab}>{`Uitslag ${tab}`}</option>
                ))}
              </select>
            )}
          </div>
        )}
        {Object.entries(uitslagenMeta).map(([jaar, meta]) => (
          meta && meta.verslag && meta.verslag.url && meta.verslag.linktekst && (
            <a
              key={jaar}
              href={meta.verslag.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                position: 'absolute',
                right:
                  24 + 120 * (
                    Object.keys(uitslagenMeta).length
                    - Object.keys(uitslagenMeta).indexOf(jaar)
                    - 1
                  ),
                top: 24,
                fontSize: '0.98em',
                color: '#fff',
                textDecoration: 'underline',
                opacity: 0.85,
                marginLeft: 16,
                maxWidth: 160,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {meta.verslag.linktekst.length > 32
                ? `${meta.verslag.linktekst.slice(0, 29)}…`
                : meta.verslag.linktekst}
            </a>
          )
        ))}
      </header>
      <main>
        <div style={{ display: 'flex', gap: '1em', alignItems: 'center', margin: '1em 0' }}>
          <div style={{ position: 'relative', width: '100%' }}>
            <input
              type="text"
              placeholder="Zoek op naam, startnummer of woonplaats"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '90%',
                fontStyle: searchTerm ? 'normal' : 'italic',
                color: searchTerm ? '#222' : '#aaa',
                paddingRight: '2.5em', // ruimte voor het kruisje
                boxSizing: 'border-box',
              }}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                style={{
                  position: 'absolute',
                  right: '11%', // zodat het kruisje binnen de input valt
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.2em',
                  color: '#aaa',
                  padding: 0,
                  lineHeight: 1,
                  zIndex: 2,
                }}
                aria-label="Zoekveld wissen"
                tabIndex={0}
                type="button"
              >
                ×
              </button>
            )}
          </div>
          <label htmlFor="onderdeel-select" style={{ display: 'flex', alignItems: 'center', gap: '0.3em', fontSize: '1em' }}>
            Onderdeel
            <select id="onderdeel-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={{ fontSize: '1em', padding: '0.3em 0.7em' }} disabled={!hasTypeFilter && !hasFinale}>
              <option value="alle">Alle</option>
              {showNkFilter && <option value="nk">NK</option>}
              <option value="dubbel">Dubbel</option>
              <option value="enkel">Enkel</option>
              {hasFinale && <option value="finale">Finale</option>}
            </select>
          </label>
          <label htmlFor="geslacht-select" style={{ display: 'flex', alignItems: 'center', gap: '0.3em', fontSize: '1em' }}>
            Geslacht
            <select id="geslacht-select" value={genderFilter} onChange={(e) => setGenderFilter(e.target.value)} style={{ fontSize: '1em', padding: '0.3em 0.7em' }} disabled={!hasGender}>
              <option value="alle">Alle</option>
              {categorieOpties.includes('M') && <option value="man">Man</option>}
              {categorieOpties.includes('V') && <option value="vrouw">Vrouw</option>}
              {categorieOpties.filter(opt => opt !== 'M' && opt !== 'V').map(opt => (
                <option key={opt} value={opt.toLowerCase()}>{opt}</option>
              ))}
            </select>
          </label>
        </div>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '2em' }}>Gegevens ophalen...</div>
        ) : uitslagenMeta[activeTab] ? (
          <div className="meta-info" style={{ margin: '1em 0', fontSize: '0.95em', color: '#444' }}>
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1.5em' }}>
              <span>
                📅&nbsp;Jaar:&nbsp;
                {uitslagenMeta[activeTab].jaar}
              </span>
              <span title={uitslagenMeta[activeTab].editie}>
                {uitslagenMeta[activeTab].editie}
                {uitslagenMeta[activeTab].canonicalUrl && (
                  <>
                    {' '}
                    <a
                      href={uitslagenMeta[activeTab].canonicalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'underline', color: 'inherit', marginLeft: 8 }}
                      title="Originele uitslag"
                    >
                      Originele uitslag
                    </a>
                  </>
                )}
              </span>
              <span>
                👥&nbsp; Aantal deelnemers:&nbsp;
                {uitslagenMeta[activeTab].aantalDeelnemers}
              </span>
            </div>
            {uitslagenMeta[activeTab].opmerking && uitslagenMeta[activeTab].opmerking.trim() !== '' && (
              <details style={{ marginTop: '0.6em' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  Event samenvatting {uitslagenMeta[activeTab].jaar}
                </summary>
                <div style={{ color: '#222', marginTop: '0.5em' }}>
                  Opmerking:
                  {' '}
                  <em>{uitslagenMeta[activeTab].opmerking}</em>
                </div>
              </details>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '2em' }}>
            Geen meta-informatie beschikbaar voor deze uitslag.
          </div>
        )}
        <div className="uitslagen-tabel-wrapper">
          <UitslagenTabel
            rows={rows}
            error={uitslagenErrors[activeTab]}
            loading={loading}
            originalRows={activeRows}
            searchTerm={searchTerm}
          />
        </div>
      </main>
    </div>
  );
}

export default UitslagenArchief;
